const PGAdapter = require('../../common/pg-adapter')
const isPGUsageErrorCode = require('../../common/isPGUsageErrorCode')
const {
  getWhereClause,
  getOrderByClause,
  getListArgs
} = require('../../tools/sql-tools')
const keyBy = require('lodash/keyBy')

const {Format, serialize} = require('./serialize-pg-result')

// The product-pg-adapter provides access to the avocado.vw_products table.
// The `getOne` and `getList` methods take an optional `format`
// paramter. The `format` controls the shape of the products returned.
// See the `./serialize-pg-result` file for format options.

const columns = [
  'product_id',
  'order_sku',
  'market',
  'product',
  'category',
  'inventory_sensitivity',
  'display_order',
  'phase',
  'available',
  'ready',
  'promoted',
  'ppmv',
  'ppmv_core',
  'therapeutic_class',
  'api',
  'substitutes',
  'manufacturer',
  'distributor',
  'supplier',
  'presentation_packaging',
  'visibility_1',
  'markup_1',
  'segments',
  'presentation_id',
  '_deleted',
  'ean',
  'hsn',
  'cold_chain',
  'scannable',
  'serialized',
  'minimum_shelf_life',
  'product_shelf_life',
  'customer_shelf_life'
]

const searchColumns = [
  'product',
  'product_id'
]

// Postgres function to do bankers rounding or like its also called ROUND_HALF_EVEN
// Implementation copied from:
// https://stackoverflow.com/questions/22613702/postgresql-roundv-numeric-s-int/30546518#30546518
const ROUND_HALF_EVEN_FN = `
  create or replace function pg_temp.round_half_even(val numeric, prec integer)
    returns numeric
  as $$
  declare
    retval numeric;
    difference numeric;
    even boolean;
  begin
    retval := round(val,prec);
    difference := retval-val;
    if abs(difference)*(10::numeric^prec) = 0.5::numeric then
      even := (retval * (10::numeric^prec)) % 2::numeric = 0::numeric;
      if not even then
        retval := round(val-difference,prec);
      end if;
    end if;
    return retval;
  end;
  $$ language plpgsql immutable strict;
`

class ProductPGAdapter extends PGAdapter {
  constructor (logger, pgConnection, username, priceAdapter) {
    super(pgConnection, 'avocado.vw_products', username, columns, 'product_id', {}, logger, {searchColumns})
    this.Format = Format
    this.priceAdapter = priceAdapter
  }

  // Overwrite `PGAdapter::getOne` to include prices in result
  async getOne (id, {whereCondition = this.idColumn, format = Format.Postgres} = {}) {
    if (!this.columns.includes(whereCondition)) {
      throw new Error(`Column ${whereCondition} not allowed`)
    }
    const query = `
      SELECT product.*
      FROM avocado.vw_products product
             JOIN avocado.data_market market ON product.market = market.product_market_code
      WHERE ${whereCondition} = $1
    `
    let result
    try {
      result = await this.pgConnection.query(query, [id])
    } catch (err) {
      if (isPGUsageErrorCode(err.code)) {
        err.status = 400
      }
      throw err
    }
    if (result.rows.length === 0) {
      const err = new Error('Row not found')
      err.status = 404
      throw err
    }
    const row = serialize(result.rows, format)[0]
    return row
  }

  // Overwrite `PGAdapter::getList` to include prices in result
  async getList ({ordering = this.idColumn, filter = {}, limit = 50, offset = 0, format = Format.Postgres, search = null} = {}) {
    // todo: a lot of this is copied from pg-adapter and could probably be replaced
    // by customizing the pg-adapter `tableExpression` and moving the function to the schema
    const searchOpts = search && {
      text: search,
      columns: this.searchColumns
    }
    const {whereText, whereValues} = getWhereClause({
      filter,
      preparers: {
        market: (key, value) => ([
          // Special case NAS products until this is resolved:
          // https://github.com/fielded/field-supply/issues/5123
          {
            statement: `
              (market = %INDEX% OR %INDEX% = ANY(market_aliases))
            `,
            value
          }
        ])
      },
      search: searchOpts
    })
    const sortText = getOrderByClause(ordering, this.columns)
    const values = [...whereValues, limit, offset]
    // Get products with json array of prices
    const query = `
     WITH products AS (
    SELECT *
    FROM avocado.vw_products p
    JOIN (select product_market_code, aliases AS market_aliases from avocado.data_market) m
      ON p.market = m.product_market_code
    ${whereText}
),

aliases AS (
    SELECT
        p.product_id,
        json_agg(
            json_build_object(
                'market', g.market,
                'sku', g.product_id
            )
        ) AS alias
    FROM products p
    JOIN products g
        ON g.presentation_id = p.presentation_id
       AND g.product_id <> p.product_id
       AND g._deleted IS false
    WHERE p._deleted IS false
    GROUP BY p.product_id
),

product_market AS (
    SELECT
        p.product_id,
        m.id AS market_id,
        m.aliases,
        m.product_market_code
    FROM products p
    JOIN avocado.data_market m
      ON p.market = m.product_market_code
),

price_rows AS (
    SELECT
        ph.sku,
        ph.date,

        pg_temp.round_half_even(ph.sellprice, 2) AS sellprice,
        pg_temp.round_half_even(ph.buyprice, 2) AS buyprice,
        pg_temp.round_half_even(ph.discount, 2) AS discount,

        pg_temp.round_half_even(
            ph.markup * markup.financing_multiplier *
            (ph.sellprice /
            (1 + ph.markup +
             markup.financing_multiplier * ph.markup +
             markup.delivery_multiplier * ph.markup)),
        2) AS financingMarkup,

        pg_temp.round_half_even(
            ph.markup * markup.delivery_multiplier *
            (ph.sellprice /
            (1 + ph.markup +
             markup.financing_multiplier * ph.markup +
             markup.delivery_multiplier * ph.markup)),
        2) AS deliveryMarkup

    FROM real.price_history ph
    JOIN product_market pm
        ON pm.product_id = ph.sku

    LEFT JOIN avocado.data_markupmultiplierhistory markup
        ON ph.date = markup.date
       AND (
            markup.market_id = ANY(pm.aliases)
            OR markup.market_id = pm.market_id
       )
),

prices AS (
    SELECT
        sku,
        json_agg(
            json_build_object(
                'date', date,
                'sellprice', sellprice,
                'buyprice', buyprice,
                'discount', discount,
                'financingMarkup', financingMarkup,
                'deliveryMarkup', deliveryMarkup
            )
            ORDER BY date
        ) AS prices
    FROM price_rows
    GROUP BY sku
) , 

vw as (
SELECT
    p.*,
    m.aliases AS market_aliases,
    pr.prices,
    a.alias
FROM products p
LEFT JOIN aliases a
    ON p.product_id = a.product_id
JOIN avocado.data_market m
    ON p.market = m.product_market_code
LEFT JOIN prices pr
    ON pr.sku = p.product_id
)    
      SELECT *
      FROM vw  ${sortText}
      LIMIT $${values.length - 1}
      OFFSET $${values.length}
    `
    let rows = []
    let count = 0

    const useOwnClient = !('connection' in this.pgConnection)
    const client = useOwnClient ? await this.pgConnection.connect() : this.pgConnection
    try {
      await client.query('BEGIN')
      // timeout after 10min
      await client.query('SET LOCAL statement_timeout TO 600000')
      await client.query(ROUND_HALF_EVEN_FN)
      const result = await client.query(query, values)
      await client.query('COMMIT')
      rows = result.rows
      count = await this.getCount(whereText, whereValues)
    } catch (err) {
      try { await client.query('ROLLBACK') } catch (_) {}
      console.log(err)
      throw err
    } finally {
      if (useOwnClient) {
        client.release()
      }
    }
    const results = serialize(rows, format)
    return {
      results,
      count,
      next: offset + rows.length
    }
  }

  async getListV2 ({ordering = this.idColumn, filter = {}, limit = 50, offset = 0, format = Format.Postgres, search = null, fetchPrices = false} = {}) {
    // todo: a lot of this is copied from pg-adapter and could probably be replaced
    // by customizing the pg-adapter `tableExpression` and moving the function to the schema
    const searchOpts = search && {
      text: search,
      columns: this.searchColumns
    }
    const {whereText, whereValues} = getWhereClause({
      filter,
      preparers: {
        market: (key, value) => ([
          // Special case NAS products until this is resolved:
          // https://github.com/fielded/field-supply/issues/5123
          {
            statement: `
              (market = %INDEX% OR %INDEX% = ANY(market_aliases))
            `,
            value
          }
        ])
      },
      search: searchOpts
    })
    const sortText = getOrderByClause(ordering, this.columns)
    const values = [...whereValues, limit, offset]
    const query = `
      WITH products AS (
        SELECT *
        FROM avocado.vw_products p
        JOIN (select product_market_code, aliases AS market_aliases from avocado.data_market) m
          ON p.market = m.product_market_code
        ${whereText} 
        ${sortText}
      LIMIT $${values.length - 1}
      OFFSET $${values.length}
      ),
      aliases AS (
        SELECT
            p.product_id,
            json_agg(
                json_build_object(
                    'market', g.market,
                    'sku', g.product_id
                )
            ) AS alias
        FROM products p
        JOIN products g
            ON g.presentation_id = p.presentation_id
           AND g.product_id <> p.product_id
           AND g._deleted IS false
        WHERE p._deleted IS false
        GROUP BY p.product_id
      ),
      product_market AS (
        SELECT
          p.product_id,
          m.id AS market_id,
          m.aliases,
          m.product_market_code
        FROM products p
        JOIN avocado.data_market m
          ON p.market = m.product_market_code
      ), 
      vw as (
        SELECT
            p.*,
            m.aliases AS market_aliases,
            a.alias
        FROM products p
        LEFT JOIN aliases a
            ON p.product_id = a.product_id
        JOIN avocado.data_market m
            ON p.market = m.product_market_code
      )
      SELECT *
      FROM vw  
    `
    let rows = []
    let count = 0
    let productPricesById = {}

    try {
      const result = await this.pgConnection.query(query, values)
      rows = result.rows
      if (fetchPrices && result.rows.length > 0) {
        const { prices } = await this.priceAdapter.getPriceList({ skus: rows.map(r => r.product_id) })
        productPricesById = keyBy(prices, 'sku')
      }
      count = await this.getCount(whereText, whereValues)
    } catch (err) {
      console.log(err)
      throw err
    }
    const results = serialize(rows, format, productPricesById)
    return {
      results,
      count,
      next: offset + rows.length
    }
  }

  async getCount (whereText = '', whereValues = []) {
    const query = `
      WITH vw as (SELECT p.*, m.aliases as market_aliases
                  FROM avocado.vw_products p
                         JOIN avocado.data_market m ON p.market = m.id OR p.market = ANY (m.aliases))
      SELECT COUNT(*)
      FROM vw ${whereText}
    `
    const result = await this.pgConnection.query(query, whereValues)
    return parseInt(result.rows[0].count)
  }

  async getAliases (skus, serviceId, pgClient) {
    if (!Array.isArray(skus) || !skus.length) {
      return {}
    }
    const params = [...skus]
    const {args, nextOffset} = getListArgs(params)
    let serviceIdFilter = ''
    if (serviceId) {
      params.push(serviceId)
      serviceIdFilter = `and m.fs_service_id = $${nextOffset}`
    }
    const client = pgClient || this.pgConnection
    const {rows} = await client.query(`
      WITH base_product AS (
        SELECT
          ps.old_sku_id,
          ps.presentation_id,
          pp.product_id,
          m.product_market_code AS market,
          m.fs_service_id
        FROM avocado.data_productsku ps
        LEFT JOIN avocado.data_productpresentation pp
          ON ps.presentation_id = pp.id
        JOIN avocado.data_market m
          ON ps.market_id = m.id
        WHERE ps.old_sku_id IN (${args})
          AND COALESCE(ps._deleted, false) IS false
        ),
        product_aliases AS (
          SELECT
            bp.old_sku_id AS base_product_id,
            m.product_market_code AS market,
            m.fs_service_id AS "serviceId",
            ps.old_sku_id AS "productId"
          FROM base_product bp
          LEFT JOIN avocado.data_productsku ps
            ON bp.presentation_id = ps.presentation_id
          LEFT JOIN avocado.data_market m
            ON ps.market_id = m.id
          WHERE COALESCE(ps._deleted, false) IS false ${serviceIdFilter}
        )
        SELECT COALESCE(
          json_object_agg(base_product_id, aliases)::jsonb,
          '{}'::jsonb
        ) AS product_aliases
        FROM (
          SELECT
            base_product_id,
            json_agg(
              json_build_object(
                'market', market,
                'serviceId', "serviceId",
                'productId', "productId"
              )
              ORDER BY market, "productId"
            ) AS aliases
          FROM product_aliases
          GROUP BY base_product_id
        ) grouped;
      `, params)
    return rows[0] || {}
  }

  async translateMarketSkus ({products, market}) {
    if (!market || !products || !products.length) {
      return {}
    }

    const query = `
      WITH sku_presentation AS (SELECT old_sku_id as sku,
                                       presentation_id
                                FROM avocado.data_productsku p
                                WHERE p.old_sku_id = ANY ($1::text[])
                                  AND p._deleted = false),
           market_translation AS (SELECT sp.sku,
                                         cm.sku as translated_sku
                                  FROM sku_presentation sp
                                         LEFT join lateral (
                                    SELECT ps2.old_sku_id AS sku
                                    from avocado.data_productsku ps1
                                           INNER JOIN avocado.data_productsku ps2
                                                      ON ps2.market_id = (SELECT id FROM avocado.data_market WHERE state = $2 LIMIT 1)
        AND ps2.presentation_id = ps1.presentation_id
      WHERE ps1.old_sku_id = sp.sku
      ORDER BY ps2._deleted nulls first,
        ps2.visibility = 'Discontinued'
        LIMIT 1
        ) cm
      ON true
        )
      SELECT json_object_agg(mt.sku, mt.translated_sku) AS translations
      FROM market_translation mt;
    `

    let result = {}
    try {
      const {rows} = await this.pgConnection.query(query, [products, market])
      if (rows.length) {
        result = rows[0].translations
      }
    } catch (e) {
      if (isPGUsageErrorCode(e.code)) {
        e.status = 400
      }
      throw e
    }
    return result
  }

  async create () {
    throw new Error('Create not implemented')
  }

  async update () {
    throw new Error('Update not implemented')
  }

  async upsert () {
    throw new Error('Upsert not implemented')
  }

  async delete () {
    throw new Error('Delete not implemented')
  }
}

module.exports = {
  Format,
  ProductPGAdapter
}
